/**
 * Signing in, against the identity provider's hosted pages.
 *
 * `deployment_lld.md` §7.2, `deployment_hld.md` §5. Story VC-017.
 *
 * Authorization code with PKCE, and no password ever reaches this code: the
 * provider hosts the form, and this module deals only in a redirect, a code
 * and the tokens that come back. Sign-up, password reset and second-factor
 * enrolment are its pages too.
 *
 * Everything here is a pure function over values, including the clock. The
 * browser half — redirecting, storing, retrying — is `session.js`, and is
 * the part this container cannot test.
 */

/** How much clock skew a token's expiry is trusted within, in seconds. */
export const EXPIRY_SKEW_SECONDS = 60;

/** What is asked for, and no more. */
export const SCOPES = ["openid", "email"];

/**
 * Base64url with no padding, from bytes.
 *
 * Hand-written because the standard encoder produces base64 with padding
 * and `+/` where this needs `-_`, and the three-character fixup is longer
 * than the loop that avoids it.
 */
export function base64url(bytes) {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * A code verifier: 32 random bytes, base64url.
 *
 * `random` is injected so a test is deterministic. It fills a `Uint8Array`,
 * which is `crypto.getRandomValues`' own shape.
 */
export function createVerifier(random = (array) => crypto.getRandomValues(array)) {
  const bytes = new Uint8Array(32);
  random(bytes);
  return base64url(bytes);
}

/**
 * The challenge for a verifier: base64url of its SHA-256.
 *
 * S256 and never `plain`. A `plain` challenge is the verifier itself, which
 * makes PKCE protect nothing: anyone who sees the authorization request has
 * what they would need to redeem the code.
 */
export async function challengeFor(verifier, digest = defaultDigest) {
  return base64url(new Uint8Array(await digest(verifier)));
}

function defaultDigest(value) {
  return crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
}

/**
 * Where to send the browser.
 *
 * The verifier is *not* in it — only its challenge. That is the whole
 * point: the verifier stays in the tab, and a URL that leaked would not
 * let its holder redeem the code.
 */
export function authorizeUrl({ domain, clientId, redirectUri }, { challenge, state }) {
  const url = new URL("/oauth2/authorize", domain);
  url.search = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: SCOPES.join(" "),
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  }).toString();
  return url.toString();
}

/** Where to send the browser to end the provider's session too. */
export function logoutUrl({ domain, clientId, redirectUri }) {
  const url = new URL("/logout", domain);
  url.search = new URLSearchParams({
    client_id: clientId,
    logout_uri: redirectUri,
  }).toString();
  return url.toString();
}

/**
 * What came back on the redirect.
 *
 * Returns one of `{kind:"code"}`, `{kind:"error"}` or `{kind:"none"}`.
 * `state` is returned rather than checked here: the value it must equal is
 * the tab's, and this function has no tab.
 */
export function readCallback(search) {
  const params = new URLSearchParams(search);
  if (params.has("error")) {
    return {
      kind: "error",
      // The provider's own code, which is a label. Its description is not
      // read: it is attacker-influenced text that would be rendered.
      error: params.get("error"),
      state: params.get("state"),
    };
  }
  if (params.has("code")) {
    return { kind: "code", code: params.get("code"), state: params.get("state") };
  }
  return { kind: "none" };
}

/**
 * The tokens, as this client holds them.
 *
 * `expiresAt` is absolute, computed once from the response. Keeping the
 * duration instead would mean recomputing a deadline on every check, which
 * is where the off-by-one lives.
 */
export function tokensFrom(response, nowSeconds) {
  return {
    accessToken: response.access_token,
    refreshToken: response.refresh_token ?? null,
    expiresAt: nowSeconds + Number(response.expires_in ?? 0),
  };
}

/**
 * Whether the access token should be exchanged before it is used again.
 *
 * True *before* expiry, by the skew: a token that expires during the
 * request it was attached to is a 401 the user sees.
 */
export function needsRefresh(tokens, nowSeconds, skew = EXPIRY_SKEW_SECONDS) {
  if (!tokens) return false;
  return tokens.expiresAt - skew <= nowSeconds;
}

/** Whether there is any way back without sending the user to the provider. */
export function canRefresh(tokens) {
  return Boolean(tokens?.refreshToken);
}

/**
 * The form body that exchanges a code for tokens.
 *
 * The verifier goes here, in a POST, and nowhere else.
 */
export function exchangeBody({ clientId, redirectUri }, { code, verifier }) {
  return new URLSearchParams({
    grant_type: "authorization_code",
    client_id: clientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: verifier,
  });
}

/** The form body that trades a refresh token for a new access token. */
export function refreshBody({ clientId }, refreshToken) {
  return new URLSearchParams({
    grant_type: "refresh_token",
    client_id: clientId,
    refresh_token: refreshToken,
  });
}

/**
 * What to do when the server refuses a call.
 *
 * One place decides, so "the session is over" is not spelled differently in
 * each caller. A 401 with a refresh token left is worth one attempt; a 401
 * after that is the end, and anything else is not about identity at all.
 */
export function afterRefusal(status, tokens, alreadyRetried) {
  if (status !== 401) return "propagate";
  if (alreadyRetried || !canRefresh(tokens)) return "signOut";
  return "refresh";
}
